/**
 * Scope Constants and Metadata Scope Resolution for Kavach UI.
 *
 * This module defines the update scope options ('everywhere', 'here', 'cancel')
 * and manages the `station_meta_scopes` structure.
 *
 * station_meta_scopes controls per-field scope propagation:
 *   - station_id_scope ('everywhere' | 'here'):
 *       'everywhere': Station ID updates propagate across all UI tables, tag summary, and visualizer.
 *       'here':       Station ID update is isolated to the currently edited table or dialog.
 *   - station_code_scope ('everywhere' | 'here'):
 *       'everywhere': Station Code updates propagate across all tables, tag labels, and block sections.
 *       'here':       Station Code update is isolated to the currently edited table.
 *   - station_name_scope ('everywhere' | 'here'):
 *       'everywhere': Station Name updates propagate across all tables, tag labels, and block sections.
 *       'here':       Station Name update is isolated to the currently edited table.
 *   - station_title_scope ('everywhere' | 'here'):
 *       'everywhere': Station Title updates propagate across drawing headers and title tables.
 *       'here':       Station Title update is isolated to the currently edited table.
 */

export const SCOPE_EVERYWHERE = 'everywhere' as const;
export const SCOPE_HERE = 'here' as const;
export const SCOPE_CANCEL = 'cancel' as const;

export const SCOPE = {
  EVERYWHERE: SCOPE_EVERYWHERE,
  HERE: SCOPE_HERE,
  CANCEL: SCOPE_CANCEL,
} as const;

export type SyncScopeChoice = typeof SCOPE_HERE | typeof SCOPE_EVERYWHERE | typeof SCOPE_CANCEL;

/**
 * Interface representing string scope values for each metadata field.
 * 'everywhere' (SCOPE_EVERYWHERE)
 * 'here' (SCOPE_HERE)
 */
export interface StationMetaScopes {
  station_id_scope: SyncScopeChoice;
  station_code_scope: SyncScopeChoice;
  station_name_scope: SyncScopeChoice;
  station_title_scope: SyncScopeChoice;
  [key: string]: SyncScopeChoice;
}

export const DEFAULT_STATION_META_SCOPES: Readonly<StationMetaScopes> = {
  station_id_scope: SCOPE_EVERYWHERE,
  station_code_scope: SCOPE_EVERYWHERE,
  station_name_scope: SCOPE_EVERYWHERE,
  station_title_scope: SCOPE_EVERYWHERE,
};

const SCOPE_KEYS: (keyof StationMetaScopes)[] = [
  'station_id_scope',
  'station_code_scope',
  'station_name_scope',
  'station_title_scope',
];

function normalizeScopeChoice(val: any, fallback: SyncScopeChoice = SCOPE_EVERYWHERE): SyncScopeChoice {
  if (val === false || val === SCOPE_HERE) return SCOPE_HERE;
  if (val === SCOPE_CANCEL) return SCOPE_CANCEL;
  if (val === true || val === SCOPE_EVERYWHERE) return SCOPE_EVERYWHERE;
  if (typeof val === 'string') {
    const s = val.trim().toLowerCase();
    if (s === SCOPE_HERE) return SCOPE_HERE;
    if (s === SCOPE_CANCEL) return SCOPE_CANCEL;
    return SCOPE_EVERYWHERE;
  }
  return fallback;
}

/**
 * Resolves station_meta_scopes from station payload or nested station_meta_info.
 *
 * @param stationData Station payload object, array, or nested metadata.
 * @returns Fully populated StationMetaScopes object with string scope values.
 */
export function resolveStationMetaScopes(stationData?: any): StationMetaScopes {
  if (!stationData || typeof stationData !== 'object') {
    return { ...DEFAULT_STATION_META_SCOPES };
  }

  // If stationData is an array (e.g. station_meta_info list)
  if (Array.isArray(stationData)) {
    // 1. Check station_title asset
    for (const item of stationData) {
      if (item?.type_of_asset === 'station_title' && item?.station_meta_scopes) {
        return resolveStationMetaScopes(item.station_meta_scopes);
      }
    }
    // 2. Fallback to any item with station_meta_scopes
    for (const item of stationData) {
      if (item?.station_meta_scopes) {
        return resolveStationMetaScopes(item.station_meta_scopes);
      }
    }
    return { ...DEFAULT_STATION_META_SCOPES };
  }

  let existingScopes: any = null;

  // Check station_meta_info array on object
  if (Array.isArray(stationData.station_meta_info)) {
    for (const item of stationData.station_meta_info) {
      if (item?.type_of_asset === 'station_title' && item?.station_meta_scopes) {
        existingScopes = item.station_meta_scopes;
        break;
      }
    }
    if (!existingScopes) {
      for (const item of stationData.station_meta_info) {
        if (item?.station_meta_scopes) {
          existingScopes = item.station_meta_scopes;
          break;
        }
      }
    }
  } else if (stationData.station_meta_info && typeof stationData.station_meta_info === 'object') {
    existingScopes = stationData.station_meta_info.station_meta_scopes;
  }

  if (!existingScopes) {
    existingScopes = stationData.station_meta_scopes;
  }

  if (existingScopes && typeof existingScopes === 'object' && !Array.isArray(existingScopes)) {
    const result = { ...DEFAULT_STATION_META_SCOPES };
    for (const key of SCOPE_KEYS) {
      if (existingScopes[key] !== undefined) {
        result[key] = normalizeScopeChoice(existingScopes[key], DEFAULT_STATION_META_SCOPES[key]);
      }
    }
    return result;
  }

  // Fallback to legacy string scope fields
  const defaultScope = normalizeScopeChoice(
    stationData.update_scope ?? stationData.station_sync_scope,
    SCOPE_EVERYWHERE
  );

  return {
    station_id_scope: normalizeScopeChoice(stationData.station_id_scope, defaultScope),
    station_code_scope: normalizeScopeChoice(stationData.station_code_scope, defaultScope),
    station_name_scope: normalizeScopeChoice(stationData.station_name_scope, defaultScope),
    station_title_scope: normalizeScopeChoice(stationData.station_title_scope, defaultScope),
  };
}
